"use client"
import { API_ENDPOINTS } from "../../constants/config";
import { useState, useEffect } from "react"
import axiosInstance from "../../utils/axiosInstance"
import { format } from "date-fns"
import { FileUp, Trash2, Edit, FileText, Clock, Calendar as CalendarIcon, Eye, CheckCircle, XCircle, AlertCircle } from 'lucide-react'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { toast } from "@/hooks/use-toast"

export default function LeaveApplication() {
  const [leaveApplications, setLeaveApplications] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false)
  const [previewFile, setPreviewFile] = useState(null)
  const [selectedLeave, setSelectedLeave] = useState(null)
  const [formData, setFormData] = useState({
    startDate: "",
    endDate: "",
    reason: "",
  })
  const [attachments, setAttachments] = useState([])
  const [uploadedFiles, setUploadedFiles] = useState([])

  useEffect(() => {
    fetchLeaveApplications()
  }, [])

  const fetchLeaveApplications = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem("token")
      const response = await axiosInstance.get(
        API_ENDPOINTS.leaveApplicationsStudent,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      setLeaveApplications(response.data.data)
      setLoading(false)
    } catch (err) {
      console.error("Error fetching leave applications:", err)
      setError(
        "Failed to fetch leave applications: " +
          (err.response?.data?.message || err.message)
      )
      setLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })
  }

  const handleDateInputChange = (e) => {
    const { name, value } = e.target
    const updatedFormData = {
      ...formData,
      [name]: value,
    }
    
    // Real-time validation: if end date is changed and is before start date, show error
    if (name === "endDate" && value && formData.startDate) {
      const startDate = new Date(formData.startDate)
      const endDate = new Date(value)
      
      // Reset time to compare only dates
      startDate.setHours(0, 0, 0, 0)
      endDate.setHours(0, 0, 0, 0)
      
      if (endDate < startDate) {
        toast({
          title: "Invalid date",
          description: "End date must be greater than or equal to start date",
          variant: "destructive",
        })
        return // Don't update if invalid
      }
    }
    
    // Real-time validation: if start date is changed and is after end date, show error
    if (name === "startDate" && value && formData.endDate) {
      const startDate = new Date(value)
      const endDate = new Date(formData.endDate)
      
      // Reset time to compare only dates
      startDate.setHours(0, 0, 0, 0)
      endDate.setHours(0, 0, 0, 0)
      
      if (endDate < startDate) {
        toast({
          title: "Invalid date",
          description: "End date must be greater than or equal to start date. Please update end date.",
          variant: "destructive",
        })
        // Clear end date if it becomes invalid
        updatedFormData.endDate = ""
      }
    }
    
    setFormData(updatedFormData)
  }

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files)
    setAttachments([...attachments, ...files])
  }

  const removeFile = (index) => {
    const newAttachments = [...attachments]
    newAttachments.splice(index, 1)
    setAttachments(newAttachments)
  }

  const removeUploadedFile = (index) => {
    const newUploadedFiles = [...uploadedFiles]
    newUploadedFiles.splice(index, 1)
    setUploadedFiles(newUploadedFiles)
  }

  const resetForm = () => {
    setFormData({
      startDate: "",
      endDate: "",
      reason: "",
    })
    setAttachments([])
    setUploadedFiles([])
  }

  const validateForm = () => {
    // Validate that dates are filled
    if (!formData.startDate || !formData.endDate) {
      toast({
        title: "Date required",
        description: "Please select both start date and end date",
        variant: "destructive",
      })
      return false
    }

    // Validate dates - end date must be >= start date
    const startDate = new Date(formData.startDate)
    const endDate = new Date(formData.endDate)
    
    // Reset time to compare only dates
    startDate.setHours(0, 0, 0, 0)
    endDate.setHours(0, 0, 0, 0)
    
    if (endDate < startDate) {
      toast({
        title: "Invalid date range",
        description: "End date must be greater than or equal to start date",
        variant: "destructive",
      })
      return false
    }

    // Validate attachments
    if (attachments.length === 0 && uploadedFiles.length === 0) {
      toast({
        title: "Attachments required",
        description: "Please upload at least one attachment",
        variant: "destructive",
      })
      return false
    }

    return true
  }

  const handleCreateSubmit = async (e) => {
    e.preventDefault()
    try {
      if (!validateForm()) return

      const token = localStorage.getItem("token")
      
     
      const formDataObj = new FormData()
      formDataObj.append("startDate", formData.startDate)
      formDataObj.append("endDate", formData.endDate)
      formDataObj.append("reason", formData.reason)
      
      attachments.forEach((file) => {
        formDataObj.append("attachments", file)
      })

      await axiosInstance.post(
        API_ENDPOINTS.leaveApplications,
        formDataObj,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      )

      toast({
        title: "Success",
        description: "Leave application submitted successfully",
      })
      
      setIsCreateDialogOpen(false)
      resetForm()
      fetchLeaveApplications()
    } catch (err) {
      console.error("Error creating leave application:", err)
      toast({
        title: "Error",
        description: "Failed to submit leave application: " + 
          (err.response?.data?.message || err.message),
        variant: "destructive",
      })
    }
  }

  const handleEditSubmit = async (e) => {
    e.preventDefault()
    try {
      if (!validateForm()) return

      const token = localStorage.getItem("token")
      
      
      const formDataObj = new FormData()
      formDataObj.append("startDate", formData.startDate)
      formDataObj.append("endDate", formData.endDate)
      formDataObj.append("reason", formData.reason)
      
      attachments.forEach((file) => {
        formDataObj.append("attachments", file)
      })

      await axiosInstance.patch(
        API_ENDPOINTS.leaveApplication(selectedLeave._id),
        formDataObj,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      )

      toast({
        title: "Success",
        description: "Leave application updated successfully",
      })
      
      setIsEditDialogOpen(false)
      resetForm()
      fetchLeaveApplications()
    } catch (err) {
      console.error("Error updating leave application:", err)
      toast({
        title: "Error",
        description: "Failed to update leave application: " + 
          (err.response?.data?.message || err.message),
        variant: "destructive",
      })
    }
  }

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("token")
      await axiosInstance.delete(API_ENDPOINTS.leaveApplication(selectedLeave._id), {
        headers: { Authorization: `Bearer ${token}` },
      })
      
      toast({
        title: "Success",
        description: "Leave application deleted successfully",
      })
      
      setIsDeleteDialogOpen(false)
      fetchLeaveApplications()
    } catch (err) {
      console.error("Error deleting leave application:", err)
      toast({
        title: "Error",
        description: "Failed to delete leave application: " + 
          (err.response?.data?.message || err.message),
        variant: "destructive",
      })
    }
  }

  const openDeleteDialog = (leave) => {
    // Don't allow deleting if already approved or rejected
    if (leave.status === "approved" || leave.status === "rejected") {
      toast({
        title: "Cannot Delete",
        description: "You cannot delete a leave application that has been approved or rejected.",
        variant: "destructive",
      })
      return
    }
    
    setSelectedLeave(leave)
    setIsDeleteDialogOpen(true)
  }

  const handleEdit = (leave) => {
    // Don't allow editing if already approved or rejected
    if (leave.status === "approved" || leave.status === "rejected") {
      toast({
        title: "Cannot Edit",
        description: "You cannot edit a leave application that has been approved or rejected.",
        variant: "destructive",
      })
      return
    }
    
    setSelectedLeave(leave)
    setFormData({
      startDate: leave.startDate.split("T")[0],
      endDate: leave.endDate.split("T")[0],
      reason: leave.reason,
    })
    setUploadedFiles(leave.attachments || [])
    setIsEditDialogOpen(true)
  }

  const handleView = (leave) => {
    setSelectedLeave(leave)
    setIsViewDialogOpen(true)
  }

  const handlePreviewFile = (file) => {
    setPreviewFile(file)
    setIsPreviewDialogOpen(true)
  }

  const getFileIconByType = (filename) => {
    const extension = filename.split('.').pop().toLowerCase()
    const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp']
    
    if (imageTypes.includes(extension)) {
      return 'image'
    }
    return 'document'
  }

  const getDuration = (startDate, endDate) => {
    const start = new Date(startDate)
    const end = new Date(endDate)
    const diffTime = Math.abs(end - start)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays + (diffDays === 1 ? " day" : " days")
  }

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
            <CheckCircle className="h-3 w-3 mr-1" />
            Approved
          </Badge>
        );
      case "rejected":
        return (
          <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
            <XCircle className="h-3 w-3 mr-1" />
            Rejected
          </Badge>
        );
      case "pending":
      default:
        return (
          <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
            <AlertCircle className="h-3 w-3 mr-1" />
            Pending
          </Badge>
        );
    }
  };


  const DatePickerNormal = ({ value, onChange, label, id, name, min }) => {
    return (
      <div className="space-y-2">
        <Label htmlFor={id}>{label}</Label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <CalendarIcon className="h-4 w-4 text-muted-foreground" />
          </div>
          <Input
            type="date"
            id={id}
            name={name}
            value={value || ""}
            onChange={onChange}
            min={min}
            className="pl-10"
            required
          />
        </div>
      </div>
    )
  }

  if (loading) {
    return <div className="flex justify-center p-8">Loading leave applications...</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold tracking-tight">Leave Applications</h2>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => resetForm()}>
              Apply for Leave
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[550px]">
            <DialogHeader>
              <DialogTitle>Apply for Leave</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateSubmit}>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <DatePickerNormal
                    id="startDate"
                    name="startDate"
                    label="Start Date"
                    value={formData.startDate}
                    onChange={handleDateInputChange}
                  />
                  <DatePickerNormal
                    id="endDate"
                    name="endDate"
                    label="End Date"
                    value={formData.endDate}
                    onChange={handleDateInputChange}
                    min={formData.startDate || undefined}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reason">Reason for Leave</Label>
                  <Textarea
                    id="reason"
                    name="reason"
                    value={formData.reason}
                    onChange={handleInputChange}
                    rows={4}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="attachments" className="flex items-center">
                    Attachments <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <Input
                    id="attachments"
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="cursor-pointer"
                    required={attachments.length === 0}
                  />
                  {attachments.length > 0 && (
                    <div className="mt-2 space-y-2">
                      <p className="text-sm font-medium">Selected Files:</p>
                      <ul className="text-sm space-y-1">
                        {attachments.map((file, index) => (
                          <li key={index} className="flex items-center justify-between bg-muted p-2 rounded-md">
                            <div className="flex items-center truncate max-w-[270px]">
                              <span className="truncate">{file.name}</span>
                            </div>
                            <div className="flex items-center">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handlePreviewFile(file)}
                                className="h-8 w-8"
                                title="Preview"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => removeFile(index)}
                                className="h-8 w-8"
                                title="Remove"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button type="submit">Submit Application</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded-r-lg">
          {error}
        </div>
      )}

      {leaveApplications.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center p-6">
            <div className="rounded-full bg-muted p-3">
              <FileText className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="mt-4 text-lg font-semibold">No leave applications</h3>
            <p className="mt-2 text-center text-muted-foreground">
              You haven't submitted any leave applications yet.
            </p>
            <Button 
              className="mt-4" 
              onClick={() => {
                resetForm()
                setIsCreateDialogOpen(true)
              }}
            >
              Apply for Leave
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date Range</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Attachments</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leaveApplications.map((leave) => (
                <TableRow key={leave._id}>
                  <TableCell>
                    <div className="flex items-center">
                      <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                      <span>
                        {format(new Date(leave.startDate), "MMM d, yyyy")} - {format(new Date(leave.endDate), "MMM d, yyyy")}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      <Clock className="mr-2 h-4 w-4 text-muted-foreground" />
                      <span>{getDuration(leave.startDate, leave.endDate)}</span>
                    </div>
                  </TableCell>
                  <TableCell className="max-w-[200px] truncate">{leave.reason}</TableCell>
                  <TableCell>
                    {getStatusBadge(leave.status)}
                  </TableCell>
                  <TableCell>
                    {leave.attachments && leave.attachments.length > 0 ? (
                      <Badge variant="outline" className="bg-blue-50 text-blue-800 hover:bg-blue-50">
                        {leave.attachments.length} {leave.attachments.length === 1 ? "file" : "files"}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground text-sm">None</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleView(leave)}
                        title="View Details"
                      >
                        <FileText className="h-4 w-4" />
                      </Button>
                      {(leave.status === "pending" || !leave.status) && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(leave)}
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openDeleteDialog(leave)}
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle>Edit Leave Application</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <DatePickerNormal
                  id="editStartDate"
                  name="startDate"
                  label="Start Date"
                  value={formData.startDate}
                  onChange={handleDateInputChange}
                />
                <DatePickerNormal
                  id="editEndDate"
                  name="endDate"
                  label="End Date"
                  value={formData.endDate}
                  onChange={handleDateInputChange}
                  min={formData.startDate || undefined}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reason">Reason for Leave</Label>
                <Textarea
                  id="reason"
                  name="reason"
                  value={formData.reason}
                  onChange={handleInputChange}
                  rows={4}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center">
                  Current Attachments <span className="text-red-500 ml-1">*</span>
                </Label>
                {uploadedFiles.length > 0 ? (
                  <ul className="text-sm space-y-1">
                    {uploadedFiles.map((file, index) => (
                      <li key={index} className="flex items-center justify-between bg-muted p-2 rounded-md">
                        <a 
                          href={file.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline truncate max-w-[270px]"
                        >
                          {file.url.split('/').pop()}
                        </a>
                        <div className="flex items-center">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => window.open(file.url, '_blank')}
                            className="h-8 w-8"
                            title="Preview"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeUploadedFile(index)}
                            className="h-8 w-8"
                            title="Remove"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">No attachments</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="newAttachments" className="flex items-center">
                  Add New Attachments {uploadedFiles.length === 0 && <span className="text-red-500 ml-1">*</span>}
                </Label>
                <Input
                  id="newAttachments"
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="cursor-pointer"
                  required={uploadedFiles.length === 0 && attachments.length === 0}
                />
                {attachments.length > 0 && (
                  <div className="mt-2 space-y-2">
                    <p className="text-sm font-medium">New Files:</p>
                    <ul className="text-sm space-y-1">
                      {attachments.map((file, index) => (
                        <li key={index} className="flex items-center justify-between bg-muted p-2 rounded-md">
                          <span className="truncate max-w-[270px]">{file.name}</span>
                          <div className="flex items-center">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handlePreviewFile(file)}
                              className="h-8 w-8"
                              title="Preview"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeFile(index)}
                              className="h-8 w-8"
                              title="Remove"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type="submit">Update Application</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle>Leave Application Details</DialogTitle>
          </DialogHeader>
          {selectedLeave && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">Start Date</h4>
                  <p>{format(new Date(selectedLeave.startDate), "MMMM d, yyyy")}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">End Date</h4>
                  <p>{format(new Date(selectedLeave.endDate), "MMMM d, yyyy")}</p>
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">Duration</h4>
                <p>{getDuration(selectedLeave.startDate, selectedLeave.endDate)}</p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">Reason</h4>
                <p className="mt-1 whitespace-pre-wrap">{selectedLeave.reason}</p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">Attachments</h4>
                {selectedLeave.attachments && selectedLeave.attachments.length > 0 ? (
                  <ul className="mt-2 space-y-2">
                    {selectedLeave.attachments.map((file, index) => (
                      <li key={index} className="flex items-center justify-between bg-muted p-2 rounded-md">
                        <div className="flex items-center truncate max-w-[350px]">
                          <FileUp className="mr-2 h-4 w-4 text-muted-foreground flex-shrink-0" />
                          <span className="truncate">{file.url.split('/').pop() || `Attachment ${index + 1}`}</span>
                        </div>
                        <div className="flex items-center">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => window.open(file.url, '_blank')}
                            className="h-8 w-8"
                            title="View Attachment"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground mt-1">No attachments</p>
                )}
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">Status</h4>
                <div className="mt-1">
                  {getStatusBadge(selectedLeave.status)}
                </div>
              </div>

              {selectedLeave.remarks && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">Remarks</h4>
                  <p className="mt-1 p-3 bg-gray-50 rounded-md whitespace-pre-wrap">{selectedLeave.remarks}</p>
                </div>
              )}

              {selectedLeave.approvedBy && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">
                    {selectedLeave.status === "approved" ? "Approved" : "Rejected"} By
                  </h4>
                  <p className="mt-1">
                    {selectedLeave.approvedBy?.firstName} {selectedLeave.approvedBy?.lastName}
                    {selectedLeave.approvedAt && (
                      <span className="text-muted-foreground ml-2">
                        on {format(new Date(selectedLeave.approvedAt), "MMMM d, yyyy 'at' h:mm a")}
                      </span>
                    )}
                  </p>
                </div>
              )}
              
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">Submitted On</h4>
                <p>{format(new Date(selectedLeave.createdAt), "MMMM d, yyyy 'at' h:mm a")}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setIsViewDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this leave application? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* File Preview Dialog */}
      <Dialog open={isPreviewDialogOpen} onOpenChange={setIsPreviewDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>File Preview</DialogTitle>
          </DialogHeader>
          {previewFile && (
            <div className="space-y-4">
              <div className="bg-muted p-4 rounded-md">
                <h3 className="font-medium mb-2">File Details</h3>
                <p><span className="font-medium">Name:</span> {previewFile.name}</p>
                <p><span className="font-medium">Type:</span> {previewFile.type || "Unknown"}</p>
                <p><span className="font-medium">Size:</span> {(previewFile.size / 1024).toFixed(2)} KB</p>
              </div>
              
              <div className="border rounded-md p-4">
                {getFileIconByType(previewFile.name) === 'image' ? (
                  <div className="flex justify-center">
                    <img 
                      src={URL.createObjectURL(previewFile)} 
                      alt={previewFile.name}
                      className="max-h-96 max-w-full object-contain" 
                    />
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center p-8 bg-muted rounded-md">
                    <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                    <p className="text-center">
                      Preview not available for this file type.<br />
                      The file will be uploaded when you submit the form.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setIsPreviewDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}